// frontend/src/components/reorderLevels/ReorderLevelsDialog.jsx
//
// The grid a merchant sets reorder levels in: a Minimum stock and an Expected
// monthly sales per item at a location (`/api/reorder-levels/`).
//
// ReorderLevelsProvider mounts one per opening and unmounts it once the
// fade-out has finished (`onExited`), so every opening starts clean: no
// search, no selection, no edits. It also hands over `currentMonth`, the
// store-local month the expected-sales cell shows (an ISO date, or null until
// the status read lands).
//
// Three ways to write, and each lands differently:
//   - a typed cell and "Set for selected" are ordinary unsaved edits, sent by
//     Save (only the edited cells);
//   - "Set for all" is one server write over every row the filters leave,
//     including rows the grid never loaded — which is why it waits for the
//     unsaved edits to be saved or discarded first.
//
// "Set for selected" writes into loaded rows, so the header checkbox ticks and
// unticks the current page only (scopeHeaderCheckboxToPage).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Tooltip, Typography,
} from '@mui/material';

import { ICON, X } from '../../icons';
import ConfirmDialog from '../common/ConfirmDialog';
import DialogHeading from '../common/DialogHeading';
import SearchField from '../common/SearchField';
import { showToast } from '../common/appToastBus';
import { GridEmptyState } from '../grid/NoRowsOverlay';
import OverrideCellRenderer from '../grid/OverrideCellRenderer';
import { itemIdentityColumns, useIsShopify } from '../grid/productIdentity';
import SetLevelsDialog from './SetLevelsDialog';
import { LocationFilter, useItemSearchCopy } from '../../features/item-filter';
import { useColumnTypes } from '../../hooks/useColumnTypes';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { useEditableGridSave } from '../../hooks/useEditableGrid';
import { useFormatters } from '../../hooks/useFormatters';
import {
  useReorderLevelsApplyToAll, useReorderLevelsBulkUpdate,
} from '../../hooks/useReorderLevelsData';
import { useSsrmGrid } from '../../hooks/useSsrmGrid';
import { useBeforeUnloadGuard } from '../../hooks/useUnsavedChangesGuard';
import { translateRequestError } from '../../queries/api';
import { useAgGridTheme } from '../../utils/agGridTheme';
import { dirtyCellPayload } from '../../utils/dirtyCellPayload';
import {
  defaultColDef, defaultGridProps, editableGridProps, copyOnlyContextMenuItems, flexColumn,
} from '../../utils/gridDefaults';
import { scopeHeaderCheckboxToPage } from '../../utils/gridSelection';
import { minimumStockColDef } from '../../utils/minimumStockColDef';
import { overrideCellTooltip } from '../../utils/overrideCellTooltip';
import { parseClearableUnitsCell } from '../../utils/planningCellParsers';
import '../../styles/dialog-form.css';

const SEARCH_DEBOUNCE_MS = 400;
const PAGE_SIZE = 100;
const GRID_HEIGHT = 420;

// The endpoint lists in one fixed order (item name, then location) and reads
// no `ordering`, so no header offers a sort.
const GRID_COL_DEF = { ...defaultColDef, sortable: false };

// A click in the expected-sales cell writes the whole planning horizon, not
// the one month the cell shows, so its hover says so.
const EXPECTED_SALES_GESTURES = {
  override: 'reorderLevels.tooltips.clickToOverride',
  reset: 'reorderLevels.tooltips.clearToReset',
};

const getRowId = (params) => String(params.data.id);

export default function ReorderLevelsDialog({ open, onClose, onExited, currentMonth }) {
  const { t } = useTranslation();
  const isShopify = useIsShopify();
  const itemSearch = useItemSearchCopy();
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const { formatMonthYear, formatNumber, parseNumber } = useFormatters();
  const { confirm, dialogProps } = useConfirmDialog();
  const gridApiRef = useRef(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [location, setLocation] = useState('all');
  const [selectedCount, setSelectedCount] = useState(0);
  // What the list last counted, kept with the filters it counted under.
  const [counted, setCounted] = useState(null);
  const [setLevelsFor, setSetLevelsFor] = useState(null);
  const [applyError, setApplyError] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setSearchText(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const ssrmParams = useMemo(() => ({ search: searchText, location }), [searchText, location]);

  // Only the block that asks for the count carries one (useSsrmGrid).
  const onData = (data) => {
    if (typeof data?.count === 'number') setCounted({ params: ssrmParams, count: data.count });
  };
  // The rows "Set for all" covers. Null until the list under the CURRENT
  // filters has answered: a count from before a filter change is another
  // list's number.
  const filteredRowCount = counted?.params === ssrmParams ? counted.count : null;

  const { onGridReady, onGridPreDestroyed, refresh, pagination } = useSsrmGrid({
    endpoint: '/api/reorder-levels/',
    params: ssrmParams,
    pageSize: PAGE_SIZE,
    gridRef: gridApiRef,
    onData,
    onError: (err) => showToast(translateRequestError(err, t, 'common.fetchError'), 'error'),
  });

  const bulkUpdate = useReorderLevelsBulkUpdate();
  const applyToAll = useReorderLevelsApplyToAll();

  // The save flow without the page's leave-page guard: the page under this
  // dialog keeps the router's one guard, and closing the dialog asks for
  // itself (requestClose).
  const editing = useEditableGridSave({
    refresh,
    getRowId: (row) => row.id,
    mutation: bulkUpdate,
    buildPayload: (_rows, changedCells) => dirtyCellPayload(changedCells),
    successKey: 'reorderLevels.messages.saved',
  });
  // A closed dialog has given its edits up, so it guards nothing as it fades.
  const { unsavedCellClass } = editing;
  useBeforeUnloadGuard(open && editing.isDirty);

  // Read off the dirty-cell map, after committing a cell still being typed
  // in: the click that asks arrives before a state update from that commit
  // could render.
  const hasUnsavedEdits = () => {
    gridApiRef.current?.stopEditing();
    return editing.getChangedCellsRef().size > 0;
  };

  // The confirmed discard clears the edits, not only the dialog: the tab's
  // unsaved-edits flag is cleared from them, and `useDirtyTabReporter`
  // (tabs/useDirtyTabs) has no cleanup, so the dialog's unmount leaves it set.
  const requestClose = async () => {
    if (hasUnsavedEdits()) {
      const ok = await confirm({
        title: t('reorderLevels.dialog.discardTitle'),
        message: t('reorderLevels.dialog.discardMessage'),
        confirmLabel: t('common.discard'),
        variant: 'warning',
      });
      if (!ok) return;
      editing.clearAll();
    }
    onClose();
  };

  // A filter change reloads the rows, so the edits typed into them go with it.
  // The selection goes too: its rows may no longer be in the list.
  const changeFilter = async (apply) => {
    if (hasUnsavedEdits()) {
      const ok = await confirm({
        title: t('common.unsavedChangesTitle'),
        message: t('reorderLevels.dialog.unsavedChangesFilter'),
        confirmLabel: t('common.discard'),
        variant: 'warning',
      });
      if (!ok) return;
      editing.clearAll();
    }
    gridApiRef.current?.deselectAll();
    apply();
  };

  const onSelectionChanged = useCallback((event) => {
    scopeHeaderCheckboxToPage(event.api);
    setSelectedCount(event.api.getSelectedRows().length);
  }, []);

  const columnDefs = useMemo(() => [
    ...itemIdentityColumns(isShopify, {
      productHeader: t('inventory.columns.product'),
      product: flexColumn(2),
      codeHeader: t('inventory.columns.itemCode'),
      skuHeader: t('inventory.columns.sku'),
      nameHeader: t('inventory.columns.itemName'),
      name: flexColumn(2),
    }),
    {
      field: 'location_name',
      headerName: t('inventory.columns.location'),
      ...flexColumn(),
    },
    {
      field: 'available',
      headerName: t('purchaseRecommendations.detail.available'),
      type: 'numberColumn',
    },
    minimumStockColDef({ t, parseNumber }),
    {
      field: 'expected_monthly_sales',
      headerName: t('reorderLevels.columns.expectedMonthlySales'),
      headerTooltip: t('reorderLevels.tooltips.expectedMonthlySales'),
      editable: true,
      type: 'numberColumn',
      cellRenderer: OverrideCellRenderer,
      cellRendererParams: {
        getSystemValue: (data) => data.system_expected_monthly_sales,
        isModified: (data, colDef) => (
          unsavedCellClass({ data, colDef })
          || Boolean(data.expected_monthly_sales_user_modified)
        ),
      },
      valueParser: (params) => parseClearableUnitsCell(params, parseNumber),
      // Without the month there is nothing to name the forecast by, so no hover.
      tooltipValueGetter: (params) => {
        const systemValue = params.data.system_expected_monthly_sales;
        if (systemValue == null || currentMonth === null) return '';
        const month = formatMonthYear(currentMonth);
        return overrideCellTooltip(
          `${t('reorderLevels.tooltips.manexysForecastFor', { month })}: ${formatNumber(systemValue, 0)}`,
          Boolean(params.data.expected_monthly_sales_user_modified),
          t,
          EXPECTED_SALES_GESTURES,
        );
      },
    },
  ], [isShopify, t, currentMonth, formatMonthYear, formatNumber, parseNumber, unsavedCellClass]);

  const closeSetLevels = () => {
    setSetLevelsFor(null);
    setApplyError(null);
  };

  const applyForAll = async (values) => {
    setApplyError(null);
    try {
      const counts = await applyToAll.mutateAsync({
        ...values,
        ...(searchText ? { search: searchText } : {}),
        ...(location === 'all' ? {} : { location }),
      });
      const done = t('reorderLevels.setForAll.done', { count: counts.pairs });
      showToast(
        counts.skipped_promotion > 0
          ? `${done} ${t('reorderLevels.setForAll.skippedPromotion', { count: counts.skipped_promotion })}`
          : done,
        'success',
      );
      closeSetLevels();
    } catch (err) {
      console.error('Reorder levels apply-to-all failed:', err.message);
      setApplyError(translateRequestError(err, t));
    }
  };

  // Written through the grid, so each value is an unsaved edit like a typed
  // one: tinted, counted, and sent by Save.
  const applyForSelected = (values) => {
    gridApiRef.current.getSelectedNodes().forEach((node) => {
      Object.entries(values).forEach(([field, value]) => node.setDataValue(field, value));
    });
    closeSetLevels();
  };

  // A fading dialog has been closed: its controls no longer act.
  const fading = !open;
  const closeLocked = editing.isSaving || fading;

  return (
    <>
      <Dialog
        open={open}
        onClose={closeLocked ? undefined : requestClose}
        className="dialog-form dialog-form--wide"
        slotProps={{ transition: { onExited } }}
      >
        <DialogTitle>
          <DialogHeading title={t('reorderLevels.title')} />
          <IconButton
            onClick={requestClose}
            size="small"
            disabled={closeLocked}
            aria-label={t('btn.close')}
          >
            <X size={ICON.xl} />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('reorderLevels.dialog.hint')}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <SearchField
              autoFocus
              label={itemSearch.label}
              placeholder={itemSearch.placeholder}
              disabled={fading}
              value={searchInput}
              onChange={(value) => changeFilter(() => setSearchInput(value))}
              fullWidth={false}
              sx={{ width: 320 }}
            />
            {/* The rows are at active locations only, so those are the only
                ones worth offering. The endpoint reads a Location id. */}
            <LocationFilter
              activeOnly
              valueKey="location_id"
              selected={location}
              onChange={(value) => changeFilter(() => setLocation(value))}
              disabled={fading}
            />
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              {/* Each span keeps its tooltip reachable while the button is disabled. */}
              <Tooltip title={editing.isDirty ? t('reorderLevels.dialog.saveFirst') : ''}>
                <span>
                  <Button
                    variant="outlined"
                    onClick={() => setSetLevelsFor('all')}
                    disabled={editing.isDirty || filteredRowCount === null || fading}
                  >
                    {t('reorderLevels.dialog.setForAll')}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={selectedCount === 0 ? t('reorderLevels.dialog.selectFirst') : ''}>
                <span>
                  <Button
                    variant="outlined"
                    onClick={() => setSetLevelsFor('selected')}
                    disabled={selectedCount === 0 || fading}
                  >
                    {t('reorderLevels.dialog.setForSelected')}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ height: GRID_HEIGHT, flexShrink: 0 }}>
            {gridTheme && (
              <GridEmptyState
                title={t('itemPicker.empty')}
                filtersActive={!!searchText || location !== 'all'}
              >
                <AgGridReact
                  {...defaultGridProps}
                  {...editableGridProps}
                  context={editing.gridContext}
                  theme={gridTheme}
                  sideBar={false}
                  columnDefs={columnDefs}
                  getRowId={getRowId}
                  defaultColDef={GRID_COL_DEF}
                  columnTypes={columnTypes}
                  onGridReady={onGridReady}
                  onGridPreDestroyed={onGridPreDestroyed}
                  onSelectionChanged={onSelectionChanged}
                  onCellValueChanged={editing.onCellValueChanged}
                  rowModelType="serverSide"
                  {...pagination}
                  rowSelection={{
                    mode: 'multiRow',
                    checkboxes: true,
                    headerCheckbox: true,
                    // The header checkbox selects every row of the list;
                    // onSelectionChanged narrows that to the current page.
                    selectAll: 'all',
                    copySelectedRows: false,
                  }}
                  getContextMenuItems={copyOnlyContextMenuItems}
                />
              </GridEmptyState>
            )}
          </Box>
        </DialogContent>

        {/* Close and Discard never stand side by side: both read as "drop my
            edits". With edits, the title bar's X is the way out, and it asks. */}
        <DialogActions>
          {editing.isDirty ? (
            <>
              <Button
                variant="outlined"
                onClick={editing.discard}
                disabled={editing.isSaving || fading}
              >
                {t('btn.discardChanges')}
              </Button>
              <Button
                variant="contained"
                onClick={editing.save}
                disabled={fading}
                loading={editing.isSaving}
              >
                {t('btn.saveChanges')} ({editing.changedRows.length})
              </Button>
            </>
          ) : (
            <Button variant="text" onClick={requestClose} disabled={closeLocked}>
              {t('btn.close')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <SetLevelsDialog
        open={setLevelsFor === 'all'}
        title={t('reorderLevels.dialog.setForAll')}
        // The button that opens this popup waits for the count.
        scope={filteredRowCount === null
          ? ''
          : t('reorderLevels.setForAll.scope', { count: filteredRowCount })}
        applying={applyToAll.isPending}
        error={applyError}
        onApply={applyForAll}
        onCancel={closeSetLevels}
      />
      <SetLevelsDialog
        open={setLevelsFor === 'selected'}
        title={t('reorderLevels.dialog.setForSelected')}
        scope={t('reorderLevels.setForSelected.scope', { count: selectedCount })}
        onApply={applyForSelected}
        onCancel={closeSetLevels}
      />
      <ConfirmDialog {...dialogProps} />
    </>
  );
}
